import React from 'react';
import moment from 'moment';
import { Users, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const ActividadGrupoCard = ({ 
    actividadGrupo, 
    onVerDetalles, 
    onCompletarTodos,
    onCompletarParticipante,
    esFechaVencida 
}) => {
    const getEstadoClasses = () => {
        if (actividadGrupo.estado_general === 'completada') {
            return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
        }
        if (actividadGrupo.estado_general === 'parcial') {
            return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
        }
        if (esFechaVencida(actividadGrupo.fecha_programada)) {
            return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
        }
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
    };

    const getEstadoText = () => {
        if (actividadGrupo.estado_general === 'completada') return 'Completada';
        if (actividadGrupo.estado_general === 'parcial') return 'Parcial';
        if (esFechaVencida(actividadGrupo.fecha_programada)) return 'Vencida';
        return 'Pendiente';
    };

    const getEstadoIcon = () => {
        if (actividadGrupo.estado_general === 'completada') {
            return <CheckCircle className="w-4 h-4" />;
        }
        if (actividadGrupo.estado_general === 'parcial') {
            return <Clock className="w-4 h-4" />;
        }
        if (esFechaVencida(actividadGrupo.fecha_programada)) {
            return <AlertCircle className="w-4 h-4" />;
        }
        return <Clock className="w-4 h-4" />;
    };

    const completadas = actividadGrupo.participantes.filter(p => p.estado === 'completada').length;
    const total = actividadGrupo.participantes.length;
    const progreso = (completadas / total) * 100;

    return (
        <div className="group bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all duration-200">
            {/* Header de la actividad */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-slate-900">
                            {actividadGrupo.nombre}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${getEstadoClasses()}`}>
                            {getEstadoIcon()}
                            {getEstadoText()}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Users className="w-4 h-4" />
                        <span>{total} participante{total !== 1 ? 's' : ''}</span>
                    </div>
                </div>
                
                <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {moment(actividadGrupo.fecha_programada).format('DD/MM/YYYY')}
                    </div>
                    
                    {/* Barra de progreso */}
                    <div className="flex items-center gap-2">
                        <div className="w-16 bg-slate-200 rounded-full h-1.5">
                            <div 
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                    progreso === 100 ? 'bg-emerald-500' : 
                                    progreso > 0 ? 'bg-amber-500' : 'bg-slate-300'
                                }`}
                                style={{ width: `${progreso}%` }}
                            ></div>
                        </div>
                        <span className="text-xs text-slate-500 font-medium">
                            {completadas}/{total}
                        </span>
                    </div>
                </div>
            </div>

            {/* Descripción */}
            {actividadGrupo.descripcion && (
                <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm text-slate-600 line-clamp-2">
                        {actividadGrupo.descripcion}
                    </p>
                </div>
            )}

            {/* Lista de participantes */}
            <div className="px-4 py-3">
                <div className="space-y-2">
                    {actividadGrupo.participantes.slice(0, 3).map((participante, index) => (
                        <div key={participante.id_actividad} className="flex items-center justify-between py-1">
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                    participante.estado === 'completada' ? 'bg-emerald-500' : 
                                    esFechaVencida(participante.fecha_programada) ? 'bg-rose-500' : 'bg-slate-300'
                                }`}></div>
                                <span className="text-sm text-slate-700">
                                    {participante.detallesNino.nombre}
                                </span>
                            </div>
                            
                            {participante.estado !== 'completada' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCompletarParticipante(actividadGrupo, participante);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                    Completar
                                </button>
                            )}
                        </div>
                    ))}
                    
                    {actividadGrupo.participantes.length > 3 && (
                        <div className="text-xs text-slate-500 pt-1">
                            +{actividadGrupo.participantes.length - 3} participante{actividadGrupo.participantes.length - 3 !== 1 ? 's' : ''} más
                        </div>
                    )}
                </div>
            </div>

            {/* Acciones */}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => onVerDetalles(actividadGrupo)}
                        className="text-sm text-slate-600 hover:text-slate-800 font-medium transition-colors"
                    >
                        Ver detalles
                    </button>
                    
                    {actividadGrupo.estado_general !== 'completada' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onCompletarTodos(actividadGrupo);
                            }}
                            className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            Completar todos
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActividadGrupoCard;